import { useState } from "react";
import { usePartnerOrders } from "@/hooks/use-partner-orders";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Package, 
  User, 
  Calendar, 
  MapPin, 
  Truck, 
  CheckCircle, 
  Clock, 
  PoundSterling,
  Search,
  Filter,
  Eye,
  CreditCard
} from "lucide-react";
import { format } from "date-fns";
import { 
  computeOrderProgress, 
  computeOrderStages, 
  getOrderStatusColor, 
  getProgressBarColor 
} from "@/utils/orderProgress";

interface PartnerOrdersListProps {
  partnerId: string;
}

export const PartnerOrdersList = ({ partnerId }: PartnerOrdersListProps) => {
  const { data: orders = [], isLoading } = usePartnerOrders(partnerId);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-32 bg-muted rounded"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  // Filter orders based on search and status
  const filteredOrders = orders.filter(order => {
    const title = order.sale?.deal?.title ? String(order.sale.deal.title).toLowerCase() : "";
    const contactEmail = order.sale?.deal?.contact?.email ? String(order.sale.deal.contact.email).toLowerCase() : "";
    const tracking = order.tracking_number ? String(order.tracking_number).toLowerCase() : "";

    const term = (searchTerm || "").toLowerCase();
    const matchesSearch = term === "" ||
      title.includes(term) ||
      contactEmail.includes(term) ||
      tracking.includes(term);
    
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const totalOrders = orders.length;
  const pendingOrders = orders.filter(o => o.status === 'pending' || o.status === 'waiting_for_payment').length;
  const processingOrders = orders.filter(o => o.status === 'order_processing').length;
  const completedOrders = orders.filter(o => o.status === 'delivered').length;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalOrders}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending Payment</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{pendingOrders}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Processing</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{processingOrders}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{completedOrders}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter Controls */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search orders by title, customer, or tracking number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="waiting_for_payment">Waiting Payment</SelectItem>
            <SelectItem value="order_processing">Processing</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <div className="text-center text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No orders found</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredOrders.map((order) => {
            const progress = computeOrderProgress(order);
            const stages = computeOrderStages(order);
            const isExpanded = expandedOrder === order.id;

            return (
              <Card key={order.id} className="overflow-hidden">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold">
                          {order.sale?.deal?.title || `Order #${order.id.slice(0, 8)}`}
                        </h3>
                        <Badge className={getOrderStatusColor(order.status)}>
                          {(order.status ?? 'pending').replace(/_/g, ' ')}
                        </Badge>
                        {order.order_type === 'credits_only' && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <CreditCard className="h-3 w-3 mr-1" />
                            Credits Only
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        {order.sale?.deal?.contact && (
                          <div className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            <span>
                              {order.sale.deal.contact.first_name} {order.sale.deal.contact.last_name}
                            </span>
                            <span className="text-xs">({order.sale.deal.contact.email})</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{format(new Date(order.created_at), 'MMM dd, yyyy')}</span>
                        </div>
                        {order.order_type !== 'credits_only' && (
                          <div className="flex items-center gap-1">
                            <Package className="h-4 w-4" />
                            <span>{order.total_cameras} cameras</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <CreditCard className="h-4 w-4" />
                          <span>{order.total_images} images</span>
                        </div>
                        {order.sale?.total_amount && (
                          <div className="flex items-center gap-1">
                            <PoundSterling className="h-4 w-4" />
                            <span>£{order.sale.total_amount}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      {isExpanded ? 'Hide' : 'Details'}
                    </Button>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {/* Progress Bar */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">Order Progress</span>
                      <span className="text-muted-foreground">{Math.round(progress)}% Complete</span>
                    </div>
                    <Progress value={progress} className={`h-2 ${getProgressBarColor(order.status)}`} />
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="space-y-6 pt-4 border-t">
                      {/* Order Timeline */}
                      <div>
                        <h4 className="text-sm font-semibold mb-4">Order Timeline</h4>
                        <div className="space-y-4">
                          {stages.map((stage, index) => {
                            const Icon = stage.icon;
                            return (
                              <div key={index} className="flex items-center gap-3">
                                <div className={`p-2 rounded-full ${
                                  stage.completed 
                                    ? 'bg-green-100 text-green-600' 
                                    : 'bg-gray-100 text-gray-400'
                                }`}>
                                  <Icon className="h-4 w-4" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${
                                      stage.completed ? 'text-foreground' : 'text-muted-foreground'
                                    }`}>
                                      {stage.label}
                                    </span>
                                    {stage.date && (
                                      <span className="text-xs text-muted-foreground">
                                        {format(new Date(stage.date), 'MMM dd, HH:mm')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Additional Order Information */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Shipping Information */}
                        {(order.delivery_address || order.tracking_number) && (
                          <div>
                            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                              <Truck className="h-4 w-4" />
                              Shipping
                            </h4>
                            <div className="space-y-2 text-sm">
                              {order.tracking_number && (
                                <div>
                                  <span className="text-muted-foreground">Tracking:</span>
                                  <span className="ml-2 font-mono">{order.tracking_number}</span>
                                </div>
                              )}
                              {order.delivery_address && (
                                <div>
                                  <span className="text-muted-foreground">Address:</span>
                                  <p className="ml-2 text-xs whitespace-pre-line">{order.delivery_address}</p>
                                </div>
                              )}
                              {order.estimated_delivery_date && (
                                <div>
                                  <span className="text-muted-foreground">Estimated Delivery:</span>
                                  <span className="ml-2">
                                    {format(new Date(order.estimated_delivery_date), 'MMM dd, yyyy')}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Order Notes */}
                        {order.fulfillment_notes && (
                          <div>
                            <h4 className="text-sm font-semibold mb-2">Notes</h4>
                            <p className="text-sm text-muted-foreground">{order.fulfillment_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};